import { BaseApiClient } from "../../lib/api/baseApiClient.service";

export interface ChatMessage {
	id: string;
	content: string;
	role: "USER" | "ASSISTANT" | "SYSTEM";
	createdAt: string;
	index: number;
	sources?: ChatSource[]; // RAG sources for assistant messages
}

export interface Chat {
	id: string;
	title: string;
	createdAt: string;
	messages: ChatMessage[];
	podWorkspaceId: string;
	messageCount?: number;
	connectedWorkspaceIds?: string[];
}

export interface ChatSource {
	source_number?: number;
	chunk_id: string;
	podcast_chapter_id: string;
	chapter_title: string;
	similarity_score: number;
	text_snippet: string;
	text_full?: string; // Full text for "Show more" functionality
	podcast_title?: string;
}

export interface SendMessageResponse {
	userMessage: ChatMessage;
	assistantMessage: ChatMessage;
	sources: ChatSource[];
	metadata: {
		tokens_used?: number;
		response_time_ms: number;
	};
}

export interface CreateChatParams {
	title: string;
	podWorkspaceId: string;
}

export interface SendMessageParams {
	query: string;
	podWorkspaceId: string;
	workspaceIds?: string[];
	config?: {
		topK?: number;
		similarityThreshold?: number;
		temperature?: number;
	};
}

/**
 * Service for interacting with the chat API
 */
export class ChatService {
	static path = "chat";

	/**
	 * Create a new chat session
	 */
	static async createChat(params: CreateChatParams): Promise<Chat> {
		return BaseApiClient.getInstance().post<Chat, CreateChatParams>(
			`${ChatService.path}/create`,
			params,
		);
	}

	/**
	 * Get chat by ID with all messages
	 */
	static async getChatById(chatId: string): Promise<Chat> {
		return BaseApiClient.getInstance().get<Chat>(
			`${ChatService.path}/${chatId}`,
		);
	}

	/**
	 * Get all chats for the current user across all workspaces
	 */
	static async getAllChats(): Promise<Chat[]> {
		return BaseApiClient.getInstance().get<Chat[]>(`${ChatService.path}/all`);
	}

	/**
	 * Get all chats for a workspace
	 */
	static async getChatsForWorkspace(workspaceId: string): Promise<Chat[]> {
		return BaseApiClient.getInstance().get<Chat[]>(
			`${ChatService.path}/workspace/${workspaceId}`,
		);
	}

	/**
	 * Send a message to a chat and get RAG response
	 */
	static async sendMessage(
		chatId: string,
		params: SendMessageParams,
	): Promise<SendMessageResponse> {
		return BaseApiClient.getInstance().postWithTimeout<
			SendMessageResponse,
			SendMessageParams
		>(`${ChatService.path}/${chatId}/message`, params, 60000); // 60 second timeout for RAG
	}

	/**
	 * Get messages for a chat
	 */
	static async getMessages(chatId: string): Promise<ChatMessage[]> {
		return BaseApiClient.getInstance().get<ChatMessage[]>(
			`${ChatService.path}/${chatId}/messages`,
		);
	}

	/**
	 * Update chat title
	 */
	static async updateChatTitle(chatId: string, title: string): Promise<Chat> {
		return BaseApiClient.getInstance().patch<Chat, { title: string }>(
			`${ChatService.path}/${chatId}/title`,
			{ title },
		);
	}

	/**
	 * Update connected workspaces for a chat
	 */
	static async updateConnectedWorkspaces(
		chatId: string,
		workspaceIds: string[],
	): Promise<Chat> {
		return BaseApiClient.getInstance().patch<Chat, { workspaceIds: string[] }>(
			`${ChatService.path}/${chatId}/workspaces`,
			{ workspaceIds },
		);
	}

	/**
	 * Delete a chat
	 */
	static async deleteChat(chatId: string): Promise<{ success: boolean }> {
		return BaseApiClient.getInstance().delete<{ success: boolean }>(
			`${ChatService.path}/${chatId}`,
		);
	}
}
